import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useParticipants } from '../hooks/useParticipants';
import { requestPin } from '../api/participantAuth';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';

const RaceLoginPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { participants, loading, error, loadParticipants } = useParticipants();
  
  const [selectedParticipant, setSelectedParticipant] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Get participantId from URL query params if available
  const urlParams = new URLSearchParams(location.search);
  const preselectedId = urlParams.get('participantId');

  useEffect(() => {
    loadParticipants();
  }, []);

  useEffect(() => {
    if (preselectedId && participants.length > 0) {
      const participant = participants.find(p => p.id === preselectedId);
      if (participant) {
        setSelectedParticipant(preselectedId);
      }
    }
  }, [preselectedId, participants]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedParticipant || !selectedMethod) {
      setSubmitError('Please select a participant and delivery method.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const participant = participants.find(p => p.id === selectedParticipant);
      
      // Validate that the participant has the selected contact method
      if (selectedMethod === 'email' && !participant.emailAddress) {
        throw new Error('This participant has no email address on file.');
      }
      if (selectedMethod === 'text' && !participant.mobile) {
        throw new Error('This participant has no mobile number on file.');
      }

      // Call the actual API to send the PIN code
      const deviceId = getDeviceFingerprint();
      const accountId = "7"; // CJC Account ID
      
      console.log('Requesting PIN for:', {
        participantId: selectedParticipant,
        method: selectedMethod,
        deviceId,
        accountId
      });

      const result = await requestPin(selectedParticipant, selectedMethod, deviceId, accountId);
      
      console.log('PIN request result:', result);

      if (result.success && result.verificationId) {
        // Navigate to PIN entry page with participant info and verificationId
        history.push('/race/pin-entry', {
          participantId: selectedParticipant,
          participantName: participant.fullName,
          deliveryMethod: selectedMethod,
          contactInfo: selectedMethod === 'email' ? participant.emailAddress : participant.mobile,
          verificationId: result.verificationId,
          expiresIn: result.expiresIn
        });
      } else {
        throw new Error(result.message || `Failed to send PIN code via ${selectedMethod}.`);
      }

    } catch (err) {
      console.error('PIN request error:', err);
      setSubmitError(err.message || 'Failed to send PIN code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedParticipantData = participants.find(p => p.id === selectedParticipant);

  if (loading) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading participants...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="alert alert-danger" role="alert">
              <h4 className="alert-heading">Error</h4>
              <p>{error}</p>
              <button className="btn btn-danger" onClick={() => loadParticipants(true)}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-6 col-md-8">
          <div className="card shadow">
            <div className="card-body p-5">
              {/* Header */}
              <div className="text-center mb-4">
                <div className="mb-3">
                  <i className="bi bi-shield-lock-fill text-primary" style={{ fontSize: '3rem' }}></i>
                </div>
                <h2 className="fw-bold mb-2">Participant Login</h2>
                <p className="text-muted">
                  Select your name and choose how to receive your secure PIN code
                </p>
              </div>

              {/* Error Alert */}
              {submitError && (
                <div className="alert alert-danger" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {submitError}
                </div>
              )}

              {/* Login Form */}
              <form onSubmit={handleSubmit}>
                {/* Participant Selection */}
                <div className="mb-4">
                  <label htmlFor="participant" className="form-label fw-semibold">
                    <i className="bi bi-person me-2"></i>
                    Select Your Name
                  </label>
                  <select
                    id="participant"
                    className="form-select form-select-lg"
                    value={selectedParticipant}
                    onChange={(e) => setSelectedParticipant(e.target.value)}
                    required
                  >
                    <option value="">Choose your name...</option>
                    {participants
                      .sort((a, b) => a.fullName.localeCompare(b.fullName))
                      .map(participant => (
                        <option key={participant.id} value={participant.id}>
                          {participant.fullName}
                          {participant.loftName && ` - ${participant.loftName}`}
                        </option>
                      ))
                    }
                  </select>
                </div>

                {/* PIN Delivery Method */}
                {selectedParticipantData && (
                  <div className="mb-4">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-send me-2"></i>
                      How would you like to receive your PIN code?
                    </label>
                    
                    <div className="mt-3">
                      {/* Email Option */}
                      {selectedParticipantData.emailAddress && (
                        <div className="form-check mb-3 p-3 border rounded">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="deliveryMethod"
                            id="email"
                            value="email"
                            checked={selectedMethod === 'email'}
                            onChange={(e) => setSelectedMethod(e.target.value)}
                          />
                          <label className="form-check-label w-100" htmlFor="email">
                            <div className="d-flex align-items-center">
                              <i className="bi bi-envelope-fill text-primary me-3" style={{ fontSize: '1.25rem' }}></i>
                              <div>
                                <div className="fw-semibold">Email</div>
                                <small className="text-muted">{selectedParticipantData.emailAddress}</small>
                              </div>
                            </div>
                          </label>
                        </div>
                      )}

                      {/* SMS Option */}
                      {selectedParticipantData.mobile && (
                        <div className="form-check mb-3 p-3 border rounded">
                          <input
                            className="form-check-input me-3"
                            type="radio"
                            name="deliveryMethod"
                            id="sms"
                            value="text"
                            checked={selectedMethod === 'text'}
                            onChange={(e) => setSelectedMethod(e.target.value)}
                          />
                          <label className="form-check-label w-100" htmlFor="sms">
                            <div className="d-flex align-items-center">
                              <i className="bi bi-phone-fill text-success me-3" style={{ fontSize: '1.25rem' }}></i>
                              <div>
                                <div className="fw-semibold">Text Message</div>
                                <small className="text-muted">{selectedParticipantData.mobile}</small>
                              </div>
                            </div>
                          </label>
                        </div>
                      )}

                      {/* No Contact Info Available */}
                      {!selectedParticipantData.emailAddress && !selectedParticipantData.mobile && (
                        <div className="alert alert-warning">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          <strong>No contact information available.</strong>
                          <br />
                          Please contact the race administrator to update your contact details.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="d-grid mb-3">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={isSubmitting || !selectedParticipant || !selectedMethod}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Sending PIN Code...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-right-circle me-2"></i>
                        Send PIN Code
                      </>
                    )}
                  </button>
                </div>

                {/* Back Button */}
                <div className="text-center">
                  <button
                    type="button"
                    className="btn btn-link text-muted"
                    onClick={() => history.goBack()}
                  >
                    <i className="bi bi-arrow-left me-1"></i>
                    Back to Participants
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RaceLoginPage;